<template>
  <div class="table-container">
    <div class="table-header">
      <h3 class="text-lg font-semibold text-gray-800">
        <i class="fas fa-table mr-2"></i>Liste des logs
      </h3>
      <div v-if="pagination.total" class="text-sm text-gray-600">
        {{ pagination.total }} résultat(s) trouvé(s)
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p class="text-gray-600 mt-4">Chargement des logs...</p>
    </div>

    <!-- Table -->
    <div v-else class="table-wrapper">
      <table class="logs-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Date/Heure</th>
            <th>Endpoint</th>
            <th>Statut</th>
            <th>Code HTTP</th>
            <th>Utilisateur</th>
            <th>Temps (ms)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="logs.length === 0">
            <td colspan="8" class="no-data">
              <i class="fas fa-inbox text-4xl text-gray-300 mb-3"></i>
              <p class="text-gray-500">Aucun log trouvé</p>
            </td>
          </tr>
          <tr v-for="log in logs" :key="log.id" class="table-row">
            <td class="log-id">#{{ log.id }}</td>
            <td class="log-date">
              <div>{{ formatDate(log.created_at) }}</div>
              <div class="text-xs text-gray-500">{{ formatTime(log.created_at) }}</div>
            </td>
            <td class="log-endpoint">
              <code class="endpoint-code">{{ log.endpoint }}</code>
            </td>
            <td>
              <span :class="['status-badge', log.status]">
                <i :class="statusIcon(log.status)"></i>
                {{ log.status === 'success' ? 'Succès' : 'Échec' }}
              </span>
            </td>
            <td>
              <span :class="['http-code', getHttpCodeClass(log.status_code)]">
                {{ log.status_code }}
              </span>
            </td>
            <td class="log-user">
              <div class="user-id" :title="log.user_id">
                <i class="fas fa-user-circle mr-1"></i>
                {{ truncateUserId(log.user_id) }}
              </div>
            </td>
            <td>
              <span :class="['response-time', getResponseTimeClass(log.response_time_ms)]">
                {{ log.response_time_ms || 0 }} ms
              </span>
            </td>
            <td class="actions-cell">
              <button @click="viewDetails(log)" class="action-btn" title="Voir les détails">
                <i class="fas fa-eye"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1" class="pagination">
      <button
        @click="goToPage(1)"
        :disabled="pagination.page === 1"
        class="pagination-btn"
      >
        <i class="fas fa-angle-double-left"></i>
      </button>
      <button
        @click="goToPage(pagination.page - 1)"
        :disabled="pagination.page === 1"
        class="pagination-btn"
      >
        <i class="fas fa-angle-left"></i>
      </button>
      
      <div class="pagination-info">
        Page {{ pagination.page }} sur {{ pagination.totalPages }}
      </div>

      <button
        @click="goToPage(pagination.page + 1)"
        :disabled="pagination.page === pagination.totalPages"
        class="pagination-btn"
      >
        <i class="fas fa-angle-right"></i>
      </button>
      <button
        @click="goToPage(pagination.totalPages)"
        :disabled="pagination.page === pagination.totalPages"
        class="pagination-btn"
      >
        <i class="fas fa-angle-double-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LogsTable',
  props: {
    logs: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      required: true,
    },
  },
  emits: ['page-change', 'view-details'],
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    },
    formatTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },
    statusIcon(status) {
      return status === 'success' ? 'fas fa-check-circle' : 'fas fa-times-circle';
    },
    getHttpCodeClass(code) {
      if (code >= 200 && code < 300) return 'success';
      if (code >= 400 && code < 500) return 'client-error';
      if (code >= 500) return 'server-error';
      return '';
    },
    getResponseTimeClass(time) {
      if (time < 100) return 'fast';
      if (time < 500) return 'medium';
      return 'slow';
    },
    truncateUserId(userId) {
      if (!userId) return 'N/A';
      return userId.length > 15 ? userId.substring(0, 15) + '...' : userId;
    },
    goToPage(page) {
      this.$emit('page-change', page);
    },
    viewDetails(log) {
      this.$emit('view-details', log);
    },
  },
};
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header {
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.loading-container {
  padding: 4rem;
  text-align: center;
}

.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #16a34a;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.table-wrapper {
  overflow-x: auto;
}

.logs-table {
  width: 100%;
  border-collapse: collapse;
}

.logs-table thead {
  background-color: #f9fafb;
}

.logs-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.logs-table td {
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  font-size: 0.875rem;
}

.table-row:hover {
  background-color: #f9fafb;
}

.log-id {
  font-weight: 600;
  color: #6b7280;
}

.log-date {
  color: #374151;
}

.log-endpoint {
  max-width: 300px;
}

.endpoint-code {
  background-color: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-family: 'Courier New', monospace;
  color: #374151;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-badge.success {
  background-color: #dcfce7;
  color: #16a34a;
}

.status-badge.failure {
  background-color: #fee2e2;
  color: #dc2626;
}

.http-code {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.75rem;
}

.http-code.success {
  background-color: #dcfce7;
  color: #16a34a;
}

.http-code.client-error {
  background-color: #fef3c7;
  color: #f59e0b;
}

.http-code.server-error {
  background-color: #fee2e2;
  color: #dc2626;
}

.log-user {
  max-width: 150px;
}

.user-id {
  color: #6b7280;
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.response-time {
  font-weight: 600;
}

.response-time.fast {
  color: #16a34a;
}

.response-time.medium {
  color: #f59e0b;
}

.response-time.slow {
  color: #dc2626;
}

.actions-cell {
  text-align: center;
}

.action-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s;
}

.action-btn:hover {
  background-color: #f3f4f6;
  color: #16a34a;
}

.no-data {
  text-align: center;
  padding: 3rem;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.pagination-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #16a34a;
  border-color: #16a34a;
  color: white;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  padding: 0.5rem 1rem;
  color: #374151;
  font-weight: 500;
  font-size: 0.875rem;
}
</style>

